/**
 * Form utilities for auto-save functionality
 */
import { SAVE_INDICATOR_DURATION_MS, AUTO_SAVE_DEBOUNCE_MS } from '../config';

// Debounce timers and pending callbacks for auto-save
const saveTimers: Record<string, NodeJS.Timeout> = {};
const pendingCallbacks: Record<string, () => void> = {};

// Centralized save indicator HTML
const SAVE_INDICATOR_SUCCESS = 'Saved <i class="fas fa-check"></i>';

/**
 * Show a save indicator (checkmark or X) on a form field
 * @param indicatorId - The ID of the indicator element
 * @param success - Whether the save was successful
 * @param message - Optional message to show (for errors)
 */
export function showSaveIndicator(indicatorId: string, success: boolean = true, message?: string): void {
  const indicator = document.getElementById(indicatorId);
  if (!indicator) return;

  // Find the parent input-with-indicator container
  const container = indicator.closest('.input-with-indicator');

  // Update indicator icon and style
  if (success) {
    indicator.innerHTML = SAVE_INDICATOR_SUCCESS;
    indicator.classList.remove('error');
    container?.classList.remove('save-error');
    container?.classList.add('save-success');
  } else {
    indicator.innerHTML = message
      ? `<i class="fas fa-times"></i> ${message}`
      : '<i class="fas fa-times"></i>';
    indicator.classList.add('error');
    container?.classList.remove('save-success');
    container?.classList.add('save-error');
  }

  indicator.classList.add('visible');

  setTimeout(() => {
    indicator.classList.remove('visible', 'error');
    indicator.innerHTML = SAVE_INDICATOR_SUCCESS;
    container?.classList.remove('save-success', 'save-error');
  }, SAVE_INDICATOR_DURATION_MS);
}

/**
 * Debounce a function call by key
 */
export function debounce(key: string, fn: () => void, delay: number = AUTO_SAVE_DEBOUNCE_MS): void {
  if (saveTimers[key]) {
    clearTimeout(saveTimers[key]);
  }
  pendingCallbacks[key] = fn;
  saveTimers[key] = setTimeout(() => {
    delete pendingCallbacks[key];
    delete saveTimers[key];
    fn();
  }, delay);
}

/**
 * Clear a specific debounce timer without executing the callback
 */
export function clearDebounce(key: string): void {
  if (saveTimers[key]) {
    clearTimeout(saveTimers[key]);
    delete saveTimers[key];
  }
  delete pendingCallbacks[key];
}

/**
 * Flush a pending debounce: execute the callback immediately and clear the timer.
 * No-op if nothing is pending for this key.
 */
export function flushDebounce(key: string): void {
  const fn = pendingCallbacks[key];
  if (fn) {
    if (saveTimers[key]) {
      clearTimeout(saveTimers[key]);
      delete saveTimers[key];
    }
    delete pendingCallbacks[key];
    fn();
  }
}

/**
 * Make radio buttons deselectable by clicking the selected option again.
 * @param container - Element containing radio buttons, or selector string
 * @param onChange - Callback when selection changes (including deselection)
 */
export function setupDeselectableRadios(
  container: Element | string,
  onChange?: (name: string, value: string) => void
): void {
  const el = typeof container === 'string' ? document.querySelector(container) : container;
  if (!el) return;

  const radios = el.querySelectorAll('input[type="radio"]') as NodeListOf<HTMLInputElement>;

  radios.forEach(radio => {
    let wasChecked = false;

    // Find the label wrapping the input (for hidden inputs with custom styling)
    const label = radio.closest('label') || radio.parentElement;

    const captureState = () => {
      wasChecked = radio.checked;
    };

    // Capture state before click on the label (input may be display:none)
    if (label) {
      label.addEventListener('mousedown', captureState);
      label.addEventListener('touchstart', captureState, { passive: true });
    }

    radio.addEventListener('click', () => {
      if (wasChecked) {
        radio.checked = false;
        // Dispatch change event so other listeners can react
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        onChange?.(radio.name, '');
      } else if (radio.checked) {
        onChange?.(radio.name, radio.value);
      }
    });
  });
}

export function wantsKidsLabel(hasKids: boolean, format: 'profile' | 'filter'): string {
  if (format === 'profile') {
    return hasKids ? 'Do you want more kids?' : 'Do you want kids?';
  }
  return hasKids ? 'Do they want more kids?' : 'Do they want kids?';
}
